// Campaign RUN STATE — the roguelike run that threads through the UMa3 map. Persisted in localStorage so
// it survives the navigation to the battle (index.html) and back. Pure logic + persistence only (no DOM,
// no THREE), so BOTH entries (campaign.html and the game index.html) import it: the campaign advances the
// run and launches battles; the game resolves a battle and writes the result back here, then returns.

import type { CampaignMap, CampaignNode } from "./graph";
import { generateSystem, type StarSystem } from "./system";
import { tempColor, type ClusterStar } from "./uma3";
import { biomeHorizonHex } from "../env/atmosphere";

export type RunStatus = "active" | "won" | "dead";

export interface PendingBattle {
  nodeId: number;
  planetIndex: number; // which world of the system this battle is on
  tier: number;        // threatTier -> game difficulty level
  battleSeed: string;  // deterministic level seed
  kind: "battle" | "elite" | "boss";
}

export interface RunState {
  seed: number;              // which campaign map (generateCampaign seed)
  currentNodeId: number;     // the SYSTEM (star node) you're at / clearing
  visited: number[];         // every system traveled to
  completed: number[];       // systems fully resolved (all worlds cleared / event resolved)
  cleared: Record<number, number[]>; // per combat system: which planet indices are cleared
  up: number;                // Unit Points (the run currency)
  hp: number;
  maxHp: number;
  status: RunStatus;
  pending: PendingBattle | null;
  log: string[];
}

const KEY = "lf_campaign_run";
const RESULT_KEY = "lf_campaign_result"; // the game writes the battle outcome here for the campaign to apply

export const RUN_TUNING = {
  maxHp: 100,
  startUp: 60,
  restHeal: 34,
  battleUp: (n: CampaignNode) => Math.round(18 + n.threatTier * 7 + n.reward * 26),
  eliteUp: (n: CampaignNode) => Math.round(30 + n.threatTier * 9 + n.reward * 34),
  shopUp: (n: CampaignNode) => Math.round(24 + n.reward * 30),
  storyUp: (n: CampaignNode) => Math.round(10 + n.reward * 18),
  battleHpCost: (n: CampaignNode) => Math.round(4 + n.threatTier * 2.2),
  eliteHpCost: (n: CampaignNode) => Math.round(8 + n.threatTier * 3),
};

export function newRun(seed: number, startId: number): RunState {
  return {
    seed, currentNodeId: startId, visited: [startId], completed: [startId], cleared: {},
    up: RUN_TUNING.startUp, hp: RUN_TUNING.maxHp, maxHp: RUN_TUNING.maxHp,
    status: "active", pending: null, log: ["// MUSTER — campaign run initialized"],
  };
}

export function loadRun(): RunState | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const rs = JSON.parse(raw) as RunState;
    if (typeof rs.seed !== "number" || !Array.isArray(rs.visited)) return null;
    if (!rs.cleared || typeof rs.cleared !== "object") rs.cleared = {}; // migrate older saves
    return rs;
  } catch { return null; }
}

// Optional durable sync (the campaign page wires this to the player's PDS after
// sign-in). localStorage stays the synchronous within-session relay — it's what
// carries `pending` across the same-browser nav to the battle and back — while
// the remote sync is the cross-device backup. The battle page never sets it, so
// it takes no atproto dependency.
let remoteSync: ((rs: RunState) => void) | null = null;
export function setRunSync(fn: ((rs: RunState) => void) | null): void { remoteSync = fn; }

export function saveRun(rs: RunState): void {
  try { localStorage.setItem(KEY, JSON.stringify(rs)); } catch { /* storage full/blocked — run is ephemeral */ }
  remoteSync?.(rs); // durable push (no-op until a session wires it via setRunSync)
}

export function clearRun(): void {
  try { localStorage.removeItem(KEY); localStorage.removeItem(RESULT_KEY); } catch { /* ignore */ }
}

/** Load the run for this campaign seed, or start a fresh one if none/mismatched. */
export function loadOrStart(map: CampaignMap): RunState {
  const rs = loadRun();
  if (rs && rs.seed === map.seed && map.nodes[rs.currentNodeId]) return rs;
  const fresh = newRun(map.seed, map.startId);
  saveRun(fresh);
  return fresh;
}

const log = (rs: RunState, msg: string) => { rs.log.push(msg); if (rs.log.length > 40) rs.log.shift(); };

export function isBattle(n: CampaignNode): boolean {
  return n.type === "battle" || n.type === "elite" || n.type === "boss";
}

// --- system geometry cache: each node's StarSystem is deterministic, so generate it once. ------------------
const sysCache = new Map<string, StarSystem>();
export function systemOf(map: CampaignMap, stars: ClusterStar[], nodeId: number): StarSystem {
  const key = `${map.seed}:${nodeId}`;
  let s = sysCache.get(key);
  if (!s) {
    const n = map.nodes[nodeId];
    s = generateSystem(stars[n.starIndex], (map.seed ^ (n.starIndex * 0x9e3779b9)) >>> 0, n.planet);
    sysCache.set(key, s);
  }
  return s;
}
export function planetCountOf(map: CampaignMap, stars: ClusterStar[], nodeId: number): number {
  return systemOf(map, stars, nodeId).planets.length;
}
export function clearedPlanets(rs: RunState, nodeId: number): number[] {
  return rs.cleared[nodeId] ?? [];
}
/** Planet indices of a combat system still to be taken. */
export function unclearedPlanets(map: CampaignMap, rs: RunState, stars: ClusterStar[], nodeId: number): number[] {
  const done = clearedPlanets(rs, nodeId);
  const pc = planetCountOf(map, stars, nodeId);
  return Array.from({ length: pc }, (_, i) => i).filter((i) => !done.includes(i));
}
/** A system is DONE when: (combat) every world is cleared, or (event) it has been resolved. */
export function systemDone(map: CampaignMap, rs: RunState, stars: ClusterStar[], nodeId: number): boolean {
  const n = map.nodes[nodeId];
  if (!isBattle(n)) return rs.completed.includes(nodeId);
  return clearedPlanets(rs, nodeId).length >= planetCountOf(map, stars, nodeId);
}

/** Systems reachable right now: only once you've cleared the whole current system. */
export function available(map: CampaignMap, rs: RunState, stars: ClusterStar[]): number[] {
  if (rs.status !== "active") return [];
  if (!systemDone(map, rs, stars, rs.currentNodeId)) return []; // finish the current system first
  return map.nodes[rs.currentNodeId].next.filter((id) => !rs.completed.includes(id));
}

/** Can the player deploy to a world of this system right now? The current (unfinished) combat system,
 *  or a valid next combat system they may commit to. Event systems are resolved, not deployed to. */
export function isDeployable(map: CampaignMap, rs: RunState, stars: ClusterStar[], nodeId: number): boolean {
  if (rs.status !== "active") return false;
  if (!isBattle(map.nodes[nodeId])) return false;
  if (nodeId === rs.currentNodeId) return !systemDone(map, rs, stars, nodeId);
  return available(map, rs, stars).includes(nodeId);
}
/** Can the player resolve this event (non-combat) system right now? A valid next event system. */
export function isResolvable(map: CampaignMap, rs: RunState, stars: ClusterStar[], nodeId: number): boolean {
  if (rs.status !== "active") return false;
  if (isBattle(map.nodes[nodeId])) return false;
  return available(map, rs, stars).includes(nodeId);
}

/** Resolve a NON-battle node instantly (shop/rest/anomaly/story) and advance the player onto it. */
export function resolveInstant(map: CampaignMap, rs: RunState, nodeId: number): void {
  const n = map.nodes[nodeId];
  switch (n.type) {
    case "rest": {
      const before = rs.hp;
      rs.hp = Math.min(rs.maxHp, rs.hp + RUN_TUNING.restHeal);
      log(rs, `DRYDOCK ${n.planet}: +${rs.hp - before} integrity`);
      break;
    }
    case "shop": {
      const g = RUN_TUNING.shopUp(n);
      rs.up += g;
      log(rs, `REQUISITION ${n.planet}: +${g} UP`);
      break;
    }
    case "story": {
      const g = RUN_TUNING.storyUp(n);
      rs.up += g;
      log(rs, `SIGNAL ${n.planet}: intel +${g} UP`);
      break;
    }
    case "anomaly": {
      // a small risk/reward beat keyed off the node's own reward/threat (deterministic per node)
      const roll = (n.starIndex * 2654435761) >>> 0;
      if (roll % 2 === 0) { const g = Math.round(20 + n.reward * 40); rs.up += g; log(rs, `ANOMALY ${n.planet}: cache +${g} UP`); }
      else { const d = Math.round(6 + n.threatTier * 2); rs.hp = Math.max(1, rs.hp - d); log(rs, `ANOMALY ${n.planet}: hazard -${d} integrity`); }
      break;
    }
    default:
      log(rs, `${n.type} ${n.planet}`);
  }
  advance(rs, map, nodeId);
}

/** Move the player onto a node and mark it completed. */
function advance(rs: RunState, map: CampaignMap, nodeId: number): void {
  rs.currentNodeId = nodeId;
  if (!rs.visited.includes(nodeId)) rs.visited.push(nodeId);
  if (!rs.completed.includes(nodeId)) rs.completed.push(nodeId);
  if (map.nodes[nodeId].type === "boss") { rs.status = "won"; log(rs, "STRONGHOLD BREACHED — campaign complete"); }
  saveRun(rs);
}

/** The world a battle is actually fought on: prefer a garden world, else the most temperate planet. */
function pickTargetPlanet(sys: StarSystem): StarSystem["planets"][number] | null {
  if (!sys.planets.length) return null;
  const g = sys.planets.find((p) => p.garden);
  if (g) return g;
  return sys.planets.slice().sort((a, b) => Math.abs(a.tempK - 288) - Math.abs(b.tempK - 288))[0];
}

/** The planet INDEX a battle should default to — the garden (or most temperate) world, not the innermost
 *  (planet 0 is always inside the HZ inner edge → hot). Used for the opening/tutorial world. */
export function targetPlanetIndex(sys: StarSystem): number {
  const t = pickTargetPlanet(sys);
  const i = t ? sys.planets.indexOf(t) : 0;
  return i < 0 ? 0 : i;
}

/** Begin a battle: record it as pending and return the URL to launch the game with — including the
 *  target world's biome (zone/type/temp + the real star color), which themes the battle's atmosphere. */
export function beginBattle(
  map: CampaignMap, rs: RunState, stars: ClusterStar[], nodeId: number, planetIndex = 0,
  sun?: { el: number; az: number }, // REAL local sun at the landing site (from the orbital geometry at deploy)
): string {
  const n = map.nodes[nodeId];
  // committing to this system: from here on the run is fought here until every world is cleared
  if (rs.currentNodeId !== nodeId) { rs.currentNodeId = nodeId; if (!rs.visited.includes(nodeId)) rs.visited.push(nodeId); }
  const tier = n.threatTier;
  const battleSeed = `uma3-${rs.seed}-${nodeId}-${planetIndex}`;
  const kind = n.type === "boss" ? "boss" : n.type === "elite" ? "elite" : "battle";
  rs.pending = { nodeId, planetIndex, tier, battleSeed, kind };
  saveRun(rs);
  const sys = systemOf(map, stars, nodeId);
  const target = sys.planets[planetIndex] ?? pickTargetPlanet(sys);
  const zone = target ? target.zone : "temperate";
  const ptype = target ? target.type : "ROCKY";
  const ptemp = target ? target.tempK : 250;
  const garden = !!(target && target.garden);
  const starHex = tempColor(stars[n.starIndex].tempK).getHexString();
  const params = new URLSearchParams({
    campaign: "1", node: String(nodeId), tier: String(tier), seed: battleSeed, kind,
    label: target ? target.name : n.planet,
    zone, ptype, ptemp: String(ptemp), garden: garden ? "1" : "0", star: starHex,
    // the destination's horizon sky color — themes the "landing" wash on both sides so the cut is invisible
    sky: biomeHorizonHex({ zone, ptype, ptemp, garden, starHex }),
  });
  if (sun) { params.set("sunel", String(Math.round(sun.el))); params.set("sunaz", String(Math.round(sun.az))); }
  return `/index.html?${params.toString()}`;
}

// ---- DEV: land on any biome directly (bypasses the run — for testing the battle atmosphere/terrain) ----
export interface DevWorld {
  key: string; label: string;
  zone: "hot" | "temperate" | "cold"; garden: boolean; ptype: string; ptemp: number; starHex: string;
}
// one representative world per battle biome (0 garden / 1 rocky / 2 scorched / 3 ice — same axes the
// atmosphere themes on). Star colors picked to read as that world's sun.
export const DEV_WORLDS: DevWorld[] = [
  { key: "garden", label: "GARDEN", zone: "temperate", garden: true, ptype: "TERRAN", ptemp: 288, starHex: "ffe8c0" },
  { key: "temperate", label: "TEMPERATE", zone: "temperate", garden: false, ptype: "ROCKY", ptemp: 250, starHex: "ffb060" },
  { key: "scorched", label: "SCORCHED", zone: "hot", garden: false, ptype: "ROCKY", ptemp: 430, starHex: "ffcaa0" },
  { key: "ice", label: "ICE", zone: "cold", garden: false, ptype: "ICE WORLD", ptemp: 150, starHex: "cfe0ff" },
];

/** A battle URL for a chosen biome, independent of the run (node 0, tier 1). Does NOT touch run state:
 *  the battle writes a result for node 0 that no pending expects, so it's dropped on return — the run is
 *  left exactly as it was. Same param shape as beginBattle so the game reads it identically. */
export function devBattleUrl(w: DevWorld, seed: string, sunEl: number, sunAz = 40): string {
  const params = new URLSearchParams({
    campaign: "1", node: "0", tier: "1", seed: `dev-${seed || "x"}`, kind: "battle", dev: "1",
    label: `DEV · ${w.label}`,
    zone: w.zone, ptype: w.ptype, ptemp: String(w.ptemp), garden: w.garden ? "1" : "0", star: w.starHex,
    sky: biomeHorizonHex({ zone: w.zone, ptype: w.ptype, ptemp: w.ptemp, garden: w.garden, starHex: w.starHex }),
    sunel: String(Math.round(sunEl)), sunaz: String(Math.round(sunAz)),
  });
  return `/index.html?${params.toString()}`;
}

export interface BattleResult { nodeId: number; win: boolean; }

/** The GAME writes its outcome here (from index.html), then navigates back to the campaign. */
export function writeBattleResult(r: BattleResult): void {
  try { localStorage.setItem(RESULT_KEY, JSON.stringify(r)); } catch { /* ignore */ }
}
export function readBattleResult(): BattleResult | null {
  try { const raw = localStorage.getItem(RESULT_KEY); return raw ? JSON.parse(raw) as BattleResult : null; }
  catch { return null; }
}
export function clearBattleResult(): void {
  try { localStorage.removeItem(RESULT_KEY); } catch { /* ignore */ }
}

/** Apply a returned battle result to the run (called by the campaign on load, if a result is waiting).
 *  A win TAKES that one world; the system is only SECURED once every world is cleared. */
export function applyBattleResult(map: CampaignMap, rs: RunState, stars: ClusterStar[], r: BattleResult): void {
  const n = map.nodes[r.nodeId];
  if (!n || !rs.pending || rs.pending.nodeId !== r.nodeId) return;
  const pi = rs.pending.planetIndex;
  const elite = n.type === "elite";
  const sys = systemOf(map, stars, r.nodeId);
  const pname = sys.planets[pi]?.name ?? n.planet;
  rs.pending = null;
  if (r.win) {
    const c = rs.cleared[r.nodeId] ?? (rs.cleared[r.nodeId] = []);
    if (!c.includes(pi)) c.push(pi);
    const up = elite ? RUN_TUNING.eliteUp(n) : RUN_TUNING.battleUp(n);
    const cost = elite ? RUN_TUNING.eliteHpCost(n) : RUN_TUNING.battleHpCost(n);
    rs.up += up;
    rs.hp = Math.max(1, rs.hp - cost);
    log(rs, `${elite ? "INTERDICTION" : "ENGAGEMENT"} ${pname}: victory +${up} UP, -${cost} integrity`);
    if (systemDone(map, rs, stars, r.nodeId)) {
      if (!rs.completed.includes(r.nodeId)) rs.completed.push(r.nodeId);
      log(rs, `${n.planet} SYSTEM SECURED — ${c.length} worlds held`);
      if (n.type === "boss") { rs.status = "won"; log(rs, "STRONGHOLD BREACHED — campaign complete"); }
    }
    saveRun(rs);
  } else {
    // defeat: heavy integrity hit; the world stays contested (you may redeploy). Death if integrity gone.
    const cost = Math.round((elite ? 26 : 18) + n.threatTier * 2);
    rs.hp -= cost;
    log(rs, `${pname}: DEFEAT -${cost} integrity`);
    if (rs.hp <= 0) { rs.hp = 0; rs.status = "dead"; log(rs, "FLEET LOST — run ended"); }
    saveRun(rs);
  }
}
